/*
 * One attachment as a row: what it is, what it is called, how big, and the two things a person does
 * with it — download it, or look at it without leaving the page.
 *
 * It stood inside `AttachmentList` as the timeline's file card until the conversation's file list
 * wanted the same row (UX X-A4 N13). Nothing about the row is about the timeline, so the two
 * surfaces share it rather than drawing a second one that would drift: the same controls, the same
 * names, the same preview.
 *
 * The preview reads the byte route's `?d=inline` answer, which the server gives only for the types
 * on its own allowlist, and never frames it: the page's policy frames only the render frame, which
 * holds no user data (security.md §18 R34). A picture is an `<img>`, a video the page's own player,
 * and a text file the sheet the drive's file page draws too (`core/render/FileText.tsx`), its words
 * as text and never as markup. A PDF has no preview in the row: it opens on its inline answer in a
 * tab of its own, where the browser's viewer draws it under the sandbox policy.
 *
 * The card is two lines (VISUAL.md §7.4): what it is, on a square carrying its type; its name; and
 * under the name how big it is — and, in a list away from its message, who sent it and when. A
 * caption and an open preview take the width below both.
 *
 * Its two controls are **always drawn**, because a file a person cannot see the download of is a
 * file they have to discover by hovering, and a touch screen hovers nothing (D-05 D9): *Download*,
 * and *Preview* — or, for a PDF, *Open in a new tab*. Where many rows stand together the names carry
 * the file as well, because "Download" said eight times in one column names nothing. The download
 * takes the name the byte route gives it, which maps what a disk refuses; the row does not propose
 * the stored one.
 */

import { useState, type ReactNode } from "react"
import { formatBytes } from "../../../core/i18n/format.ts"
import { FileText } from "../../../core/render/FileText.tsx"
import { Icon } from "../../../core/shell/index.ts"
import type { AttachmentWire } from "../../../core/wire/index.ts"
import { Button } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import { altOf, isPicture } from "./AttachmentList.tsx"
import "./attachments.css"

/**
 * The three or four letters on a file's type square — its extension, which is what a person reads a
 * file by. A name without one (or with something that is not a word) gets the generic glyph instead:
 * a square saying nothing is worse than a picture of a sheet of paper.
 */
export function typeLabel(filename: string): string {
	const dot = filename.lastIndexOf(".")
	const extension = dot > 0 ? filename.slice(dot + 1) : ""
	return /^[a-z0-9]{1,4}$/i.test(extension) ? extension.toUpperCase() : ""
}

export interface AttachmentRowProps {
	file: AttachmentWire
	/**
	 * A line under the name, for a row that stands away from its message: who sent the file and
	 * when, and where it was sent.
	 */
	meta?: ReactNode
	/**
	 * True where several rows stand together: each control then names its own file. In a message
	 * there is one row and the file is the row, so the short names are the right ones there.
	 */
	namedActions?: boolean
}

export function AttachmentRow({ file, meta, namedActions = false }: AttachmentRowProps) {
	const [preview, setPreview] = useState(false)
	// A thumbnail whose bytes never came leaves the browser's broken-image glyph in the square, which
	// is a fault reported in the browser's words (N5-R1-13). The type square is what the row would
	// have drawn for a file without pixels, and it is still true of this one.
	const [thumbBroken, setThumbBroken] = useState(false)
	const type = typeLabel(file.filename)
	const download = namedActions
		? t("chat.files.downloadOf", { name: file.filename })
		: t("chat.attachment.download")
	const show = namedActions
		? t("chat.files.previewOf", { name: file.filename })
		: t("chat.attachment.showPreview")
	const hide = namedActions
		? t("chat.files.hidePreviewOf", { name: file.filename })
		: t("chat.attachment.hidePreview")
	const openInTab = namedActions
		? t("chat.files.openInTabOf", { name: file.filename })
		: t("chat.attachment.openInTab")
	const inline = `${file.url}?d=inline`
	// What the row may show of the file, by what the byte route answers inline: a PDF in a tab of its
	// own, a kind the row knows how to draw under the row.
	const tab = file.inlineOk && file.kind === "pdf"
	const drawn = file.inlineOk ? PREVIEWS[file.kind] : undefined
	return (
		<div className="tb-attachments__card">
			{isPicture(file) && !thumbBroken ? (
				// The derived thumbnail, which the byte route answers with the original until the
				// variant sweep has run: a row never waits for a picture that is seconds old.
				<img
					className="tb-attachments__thumb"
					src={file.thumbUrl}
					alt={altOf(file)}
					loading="lazy"
					decoding="async"
					onError={() => setThumbBroken(true)}
				/>
			) : (
				<span className="tb-attachments__icon" aria-hidden="true">
					{type === "" ? <Icon name="file" /> : type}
				</span>
			)}
			<span className="tb-attachments__name">{file.filename}</span>
			<span className="tb-attachments__detail">
				<span className="tb-attachments__size">{formatBytes(file.bytes)}</span>
				{isPicture(file) ? (
					<span className="tb-attachments__pixels">
						{t("chat.attachment.pixels", { width: file.width ?? 0, height: file.height ?? 0 })}
					</span>
				) : null}
				{meta === undefined ? null : <span className="tb-attachments__meta">{meta}</span>}
			</span>
			<div className="tb-attachments__actions">
				<Button
					quiet
					icon
					size="sm"
					className="tb-attachments__action"
					href={file.url}
					download
					aria-label={download}
					title={download}
					before={<Icon name="download" size={16} />}
				/>
				{tab ? (
					<Button
						quiet
						icon
						size="sm"
						className="tb-attachments__action"
						href={inline}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={openInTab}
						title={openInTab}
						before={<Icon name="externalLink" size={16} />}
					/>
				) : null}
				{drawn !== undefined ? (
					<Button
						quiet
						icon
						size="sm"
						className="tb-attachments__action"
						aria-expanded={preview}
						aria-label={preview ? hide : show}
						title={preview ? hide : show}
						before={<Icon name={preview ? "eyeOff" : "eye"} size={16} />}
						onClick={() => setPreview((open) => !open)}
					/>
				) : null}
			</div>
			{file.caption === "" ? null : <span className="tb-attachments__caption">{file.caption}</span>}
			{preview && drawn !== undefined ? drawn(file, inline) : null}
		</div>
	)
}

/**
 * The open preview under a row, by the file's kind, drawn from the byte route's inline answer `src`:
 * the kinds that have one are the keys here, and a kind without one offers no *Preview*.
 */
const PREVIEWS: Partial<Record<string, (file: AttachmentWire, src: string) => ReactNode>> = {
	image: (file, src) => <img className="tb-attachments__preview" src={src} alt={altOf(file)} />,
	video: (file, src) => (
		// An uploaded video brings no captions file of its own to offer as a track.
		// eslint-disable-next-line jsx-a11y/media-has-caption
		<video
			className="tb-attachments__preview"
			title={t("chat.attachment.previewOf", { name: file.filename })}
			src={src}
			controls
			preload="metadata"
		/>
	),
	text: (file, src) => (
		<FileText className="tb-attachments__preview" src={src} name={file.filename} />
	),
}
